class NewsAndPress extends HTMLElement {
  constructor() {
    super();
    this.navLinks = [];
    this.modalLinks = [];
    this.grid = null;
    this.allCards = [];
    this.emptyState = null;
    this.mobileBannerLabel = null;
    this.mobileTriggerLabel = null;
    this.dropdownTrigger = null;
    this.modal = null;
    this.modalBackdrop = null;
    this.modalCloseBtn = null;
    this.cache = new Map();
    this.isLoading = false;
    this.pendingSelection = null;
    this.allPostsUrl = null;
  }

  connectedCallback() {
    this.init();
  }

  getAllPostsLink() {
    return [...this.navLinks, ...this.modalLinks].find(
      (link) => (link.getAttribute('data-category-target') || '').toLowerCase().trim() === 'all'
    );
  }

  normalizePath(path) {
    return path.replace(/\/+$/, '').toLowerCase();
  }

  countCardsInHtml(html) {
    return (html.match(/data-news-item/g) || []).length;
  }

  init() {
    this.navLinks = Array.from(this.querySelectorAll('.news-press-category-nav__link'));
    this.modalLinks = Array.from(this.querySelectorAll('.news-press-mobile-modal__link'));
    this.grid = this.querySelector('.template-blog__grid');
    this.allCards = Array.from(this.querySelectorAll('[data-news-item]'));
    this.emptyState = this.querySelector('[data-news-empty]');
    this.mobileBannerLabel = this.querySelector('[data-news-selected-label]');
    this.mobileTriggerLabel = this.querySelector('[data-news-trigger-label]');
    this.dropdownTrigger = this.querySelector('[data-news-dropdown-trigger]');
    this.modal = this.querySelector('[data-news-modal]');
    this.modalBackdrop = this.querySelector('[data-news-modal-backdrop]');
    this.modalCloseBtn = this.querySelector('[data-news-modal-close]');

    if (!this.grid) return;

    const currentPath = this.normalizePath(window.location.pathname);
    const allPostsLink = this.getAllPostsLink();
    this.allPostsUrl = allPostsLink ? allPostsLink.getAttribute('href') : null;

    this.cache.set(currentPath, this.grid.innerHTML);

    if (this.allPostsUrl) {
      const allPostsPath = this.normalizePath(new URL(this.allPostsUrl, window.location.origin).pathname);
      if (currentPath === allPostsPath) {
        this.cache.set(allPostsPath, this.grid.innerHTML);
      } else {
        void this.prefetchAllPosts(this.allPostsUrl);
      }
    }

    this.bindEvents();
    void this.syncFromUrl(window.location.href, false);
  }

  bindEvents() {
    // All navigation links (desktop horizontal + mobile modal)
    const allCategoryLinks = [...this.navLinks, ...this.modalLinks];
    allCategoryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (!href || href === '#' || href.startsWith('javascript:')) return;

        e.preventDefault();
        this.closeModal();
        void this.selectCategory(link, href, true);
      });
    });

    // Mobile Dropdown Trigger Open
    if (this.dropdownTrigger) {
      this.dropdownTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal();
      });
    }

    // Modal Backdrop Click Close
    if (this.modalBackdrop) {
      this.modalBackdrop.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    }

    // Modal Close Button Click
    if (this.modalCloseBtn) {
      this.modalCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeModal();
      });
    }

    // Close Modal on ESC Key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeModal();
      }
    });

    // Browser History Navigation (Back / Forward)
    window.addEventListener('popstate', () => {
      this.closeModal();
      void this.syncFromUrl(window.location.href, false);
    });

    // Theme Editor support
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        const blockId = e.detail?.blockId;
        const blockEl = this.querySelector(`[data-shopify-editor-block*="${blockId}"]`);
        const link = blockEl ? blockEl.querySelector('.news-press-category-nav__link, .news-press-mobile-modal__link') : null;
        if (link) {
          const href = link.getAttribute('href');
          this.closeModal();
          this.selectCategory(link, href, false);
        }
      });
    }
  }

  isModalOpen() {
    return this.modal && !this.modal.hasAttribute('hidden') && this.modal.style.display !== 'none';
  }

  openModal() {
    if (!this.modal) return;
    this.modal.removeAttribute('hidden');
    this.modal.style.display = 'flex';
    document.body.classList.add('news-modal-open');
    if (this.dropdownTrigger) {
      this.dropdownTrigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.setAttribute('hidden', '');
    this.modal.style.display = 'none';
    document.body.classList.remove('news-modal-open');
    if (this.dropdownTrigger) {
      this.dropdownTrigger.setAttribute('aria-expanded', 'false');
    }
  }

  syncFromUrl(urlStr, pushHistory = false) {
    const url = new URL(urlStr, window.location.origin);
    const path = this.normalizePath(url.pathname);
    const catParam = (url.searchParams.get('category') || url.hash.replace('#', '') || '').toLowerCase().trim();

    const allCategoryLinks = [...this.navLinks, ...this.modalLinks];
    let activeLink = null;

    if (catParam) {
      activeLink = allCategoryLinks.find(link => {
        const target = (link.getAttribute('data-category-target') || '').toLowerCase().trim();
        return target === catParam;
      });
    }

    if (!activeLink) {
      activeLink = allCategoryLinks.find(link => {
        const href = link.getAttribute('href');
        if (!href) return false;
        const linkPath = this.normalizePath(new URL(href, window.location.origin).pathname);
        return linkPath === path;
      });
    }

    // Default to first link (All Posts) if no match found
    if (!activeLink) {
      activeLink = this.navLinks[0] || this.modalLinks[0];
    }

    if (activeLink) {
      return this.selectCategory(activeLink, activeLink.getAttribute('href'), pushHistory);
    }
  }

  async selectCategory(activeLink, targetUrl, pushHistory = true) {
    if (this.isLoading) {
      this.pendingSelection = { activeLink, targetUrl, pushHistory };
      return;
    }

    // 1. Resolve Target Info and Label
    const resolvedUrl = new URL(targetUrl, window.location.origin);
    const targetPath = resolvedUrl.pathname;
    const targetCategory = (activeLink.getAttribute('data-category-target') || '').toLowerCase().trim();
    const isAllPosts = targetCategory === 'all' || targetPath.includes('/pages/');

    // Get clean label
    let categoryLabel = activeLink.getAttribute('data-category-label') || activeLink.querySelector('span')?.textContent || activeLink.textContent || '';
    categoryLabel = categoryLabel.trim();

    // 2. Update Active Navigation UI across desktop links and mobile modal links
    const allCategoryLinks = [...this.navLinks, ...this.modalLinks];
    allCategoryLinks.forEach(link => {
      const linkTarget = (link.getAttribute('data-category-target') || '').toLowerCase().trim();
      const isCurrent = linkTarget === targetCategory || (isAllPosts && (linkTarget === 'all' || link.getAttribute('href')?.includes('/pages/')));

      if (isCurrent) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
        link.setAttribute('aria-selected', 'true');
      } else {
        link.classList.remove('is-active');
        link.removeAttribute('aria-current');
        link.setAttribute('aria-selected', 'false');
      }
    });

    // 3. Update Mobile Top Banner and Dropdown Trigger Text
    if (categoryLabel) {
      if (this.mobileBannerLabel) {
        this.mobileBannerLabel.textContent = categoryLabel;
      }
      if (this.mobileTriggerLabel) {
        this.mobileTriggerLabel.textContent = categoryLabel;
      }
    }

    // 4. Update Browser URL using History API without page reload
    if (pushHistory && this.normalizePath(window.location.pathname) !== this.normalizePath(targetPath)) {
      window.history.pushState({ target: targetCategory, path: targetPath, label: categoryLabel }, '', resolvedUrl.href);
    }

    // 5. Restore the full aggregated grid before showing all posts
    if (isAllPosts) {
      await this.ensureAllPostsLoaded(resolvedUrl.href);
    }

    // 6. Client-side card filtering
    const visibleCount = this.applyCardFilter(targetCategory, targetPath, isAllPosts);

    // 7. If 0 matching cards and on a partial blog page, fetch missing cards asynchronously
    if (visibleCount === 0 && !isAllPosts && this.allCards.length > 0) {
      await this.fetchMissingCards(resolvedUrl.href, targetCategory, targetPath);
    }

    await this.runPendingSelection();
  }

  async runPendingSelection() {
    if (!this.pendingSelection) return;

    const pending = this.pendingSelection;
    this.pendingSelection = null;
    await this.selectCategory(pending.activeLink, pending.targetUrl, pending.pushHistory);
  }

  async prefetchAllPosts(allPostsUrl) {
    const allPostsPath = this.normalizePath(new URL(allPostsUrl, window.location.origin).pathname);
    if (this.cache.has(allPostsPath)) return;

    try {
      const response = await fetch(allPostsUrl);
      if (!response.ok) return;

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const fetchedGrid = doc.querySelector('news-and-press .template-blog__grid') || doc.querySelector('.template-blog__grid');

      if (fetchedGrid) {
        this.cache.set(allPostsPath, fetchedGrid.innerHTML);
      }
    } catch (err) {
      console.warn('Failed to prefetch all posts:', err);
    }
  }

  needsAllPostsRestore(cachedHtml) {
    const hasHiddenCards = this.allCards.some((card) => card.classList.contains('is-hidden'));
    const cachedCardCount = this.countCardsInHtml(cachedHtml);
    return hasHiddenCards || this.allCards.length < cachedCardCount;
  }

  async ensureAllPostsLoaded(allPostsUrl) {
    const allPostsPath = this.normalizePath(new URL(allPostsUrl, window.location.origin).pathname);
    const cachedHtml = this.cache.get(allPostsPath);

    if (cachedHtml) {
      if (this.needsAllPostsRestore(cachedHtml)) {
        this.grid.innerHTML = cachedHtml;
        this.refreshAllCards();
      }
      return;
    }

    try {
      this.isLoading = true;
      this.grid.style.opacity = '0.5';
      this.grid.style.transition = 'opacity 0.2s ease';

      const response = await fetch(allPostsUrl);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const htmlText = await response.text();
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const fetchedGrid = doc.querySelector('news-and-press .template-blog__grid') || doc.querySelector('.template-blog__grid');

      if (fetchedGrid) {
        this.grid.innerHTML = fetchedGrid.innerHTML;
        this.cache.set(allPostsPath, fetchedGrid.innerHTML);
        this.refreshAllCards();
      }
    } catch (err) {
      console.warn('Failed to load all posts:', err);
    } finally {
      this.isLoading = false;
      this.grid.style.opacity = '1';
    }
  }

  refreshAllCards() {
    this.allCards = Array.from(this.querySelectorAll('[data-news-item]'));
  }

  applyCardFilter(targetCategory, targetPath, isAllPosts) {
    let visibleCount = 0;

    if (isAllPosts) {
      this.refreshAllCards();
    }

    this.allCards.forEach(card => {
      if (isAllPosts) {
        card.classList.remove('is-hidden');
        visibleCount++;
        return;
      }

      const cardHandle = (card.getAttribute('data-blog-handle') || '').toLowerCase();
      const cardUrl = (card.getAttribute('data-blog-url') || '').toLowerCase();
      const cardSlug = (card.getAttribute('data-category-slug') || '').toLowerCase();

      const isMatch = this.matchesCategory(targetCategory, targetPath, cardHandle, cardUrl, cardSlug);

      if (isMatch) {
        card.classList.remove('is-hidden');
        visibleCount++;
      } else {
        card.classList.add('is-hidden');
      }
    });

    this.updateEmptyState(visibleCount);

    if (window.AOS && typeof window.AOS.refresh === 'function') {
      window.AOS.refresh();
    }

    return visibleCount;
  }

  matchesCategory(targetCategory, targetPath, cardHandle, cardUrl, cardSlug) {
    if (!targetCategory && !targetPath) return true;

    // Normalizations for news handles
    const isNewsTarget = targetCategory === 'news-and-press' || targetCategory === 'news' || targetCategory === 'news-press' || targetPath.includes('/blogs/news');
    const isNewsCard = cardHandle === 'news-and-press' || cardHandle === 'news' || cardHandle === 'news-press' || cardUrl.includes('/blogs/news') || cardSlug.includes('news');

    if (isNewsTarget && isNewsCard) return true;

    // Normalizations for product announcements handles
    const isAnnounceTarget = targetCategory === 'product-announcements' || targetCategory === 'announcements' || targetPath.includes('product-announcements') || targetPath.includes('announcements');
    const isAnnounceCard = cardHandle === 'product-announcements' || cardHandle === 'announcements' || cardUrl.includes('product-announcements') || cardSlug.includes('product-announcements') || cardSlug.includes('announcements');

    if (isAnnounceTarget && isAnnounceCard) return true;

    // Direct match
    if (targetCategory && (cardHandle === targetCategory || cardSlug.includes(targetCategory) || cardUrl.includes(targetCategory))) {
      return true;
    }

    if (targetPath && (cardUrl.includes(targetPath) || targetPath.includes(cardHandle))) {
      return true;
    }

    return false;
  }

  updateEmptyState(visibleCount) {
    if (!this.emptyState) return;

    if (visibleCount === 0) {
      this.emptyState.style.display = 'block';
      this.emptyState.removeAttribute('hidden');
    } else {
      this.emptyState.style.display = 'none';
      this.emptyState.setAttribute('hidden', '');
    }
  }

  async fetchMissingCards(url, targetCategory, targetPath) {
    try {
      this.isLoading = true;
      this.grid.style.opacity = '0.5';
      this.grid.style.transition = 'opacity 0.2s ease';

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);

      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      const fetchedCards = Array.from(doc.querySelectorAll('[data-news-item]'));
      if (fetchedCards.length > 0) {
        // Append unique fetched cards
        fetchedCards.forEach(newCard => {
          const cardKey = newCard.querySelector('a')?.getAttribute('href') || newCard.innerHTML;
          const exists = this.allCards.some(c => (c.querySelector('a')?.getAttribute('href') || c.innerHTML) === cardKey);
          if (!exists) {
            this.grid.appendChild(newCard);
            this.allCards.push(newCard);
          }
        });

        this.applyCardFilter(targetCategory, targetPath, false);
      }
    } catch (err) {
      console.warn('Failed to fetch category content dynamically:', err);
    } finally {
      this.isLoading = false;
      this.grid.style.opacity = '1';
    }
  }
}

if (!customElements.get('news-and-press')) {
  customElements.define('news-and-press', NewsAndPress);
}
